import React from 'react';
import clsx from 'clsx';

/**
 * Colour by intention, not by status: the same five tones serve every catalog.
 *
 * `error` and `default` are accepted next to `danger` and `neutral` because
 * `utils/inventoryLabels.js` names its tones that way, and that file is shared
 * with the front this one replaces.
 */
const TONES = {
  success: 'bg-ninja-greenLight dark:bg-ninja-green/20 text-ninja-green border-ninja-green/20',
  warning: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 border-orange-500/20',
  info: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-500/20',
  danger: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-red-500/20',
  neutral: 'bg-ninja-gray dark:bg-[#2d2d2d] text-text-secondary border-border',
};

const TONE_ALIASES = { error: 'danger', default: 'neutral' };

/**
 * A short label on a tinted pill: a state, or a number that carries a verdict.
 *
 * @param {object} props
 * @param {'success'|'warning'|'info'|'danger'|'neutral'|'error'|'default'} [props.tone='neutral']
 * @param {React.ComponentType} [props.icon]
 * @param {string} [props.title]
 * @param {string} [props.testId]
 * @param {React.ReactNode} props.children
 */
export const ToneBadge = ({ tone = 'neutral', icon: Icon, title, testId, children }) => {
  const resolved = TONE_ALIASES[tone] || tone;

  return (
    <span
      data-testid={testId}
      data-tone={resolved}
      title={title}
      className={clsx(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border whitespace-nowrap tabular-nums',
        TONES[resolved] ?? TONES.neutral
      )}
    >
      {Icon && <Icon size={12} />}
      {children}
    </span>
  );
};
